// ─────────────────────────────────────────────────────────────────────────────
// Global fetch override — session cookies on every call, and silent refresh
// of the backend access token.
//
// 1. Cookies. The BFF authenticates via the `nexrisk_session` HttpOnly cookie
//    (see src/middleware/auth.ts). For browsers to actually forward that cookie
//    on a fetch() call, the request must be made with `credentials: 'include'`
//    (or 'same-origin' for same-origin requests). The default is 'same-origin',
//    which silently drops the cookie on any cross-origin call — including the
//    localhost:5174 → localhost:8080 case during development. Every call
//    defaults to `credentials: 'include'`; a call can still opt out by
//    passing `credentials: 'omit'`.
//
// 2. Access-token refresh. The BFF session lives 8 h, but the C++ access
//    token it holds and forwards expires much sooner
//    (access_token_ttl_seconds). Once it expires, every BFF route that
//    forwards it answers 401 while the session itself is still alive. On a
//    401 from /api/v1/* (auth routes excluded) this wrapper calls
//    POST /api/v1/auth/refresh once — shared by all calls failing at the
//    same time — and retries the original request. If the refresh fails, the
//    BFF has already destroyed the session, so the user is sent to /login.
//
// Import this file exactly ONCE, from src/main.tsx, before any other app code
// runs. Do not import it from library code or pages — the override must be
// globally installed before the first fetch occurs.
// ─────────────────────────────────────────────────────────────────────────────

const originalFetch = window.fetch.bind(window);

/** Pages reachable without a session: never redirect away from these. */
const PUBLIC_PATHS = [
  '/login',
  '/setup',
  '/change-password',
  '/forgot-password',
  '/reset-password',
  '/terms',
  '/disclosure',
];

/** The refresh in flight, shared by every request that hit a 401 meanwhile. */
let refreshInFlight: Promise<boolean> | null = null;
/** When the last successful refresh completed (ms since epoch). */
let lastRefreshAt = 0;
/** Set once the session is known dead, so only one redirect happens. */
let sessionDead = false;

function requestUrl(input: RequestInfo | URL): URL | null {
  try {
    const raw = input instanceof Request ? input.url : String(input);
    return new URL(raw, window.location.href);
  } catch {
    return null;
  }
}

/** A 401 here means the backend token expired and a refresh may cure it. */
function isRefreshable(url: URL | null): url is URL {
  if (!url) return false;
  return url.pathname.startsWith('/api/v1/') && !url.pathname.startsWith('/api/v1/auth/');
}

/** A body that can be sent a second time unchanged. */
function isReplayable(init: RequestInit): boolean {
  return !(typeof ReadableStream !== 'undefined' && init.body instanceof ReadableStream);
}

function refreshSession(origin: string): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = originalFetch(`${origin}/api/v1/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      cache: 'no-store',
    })
      .then((res) => {
        if (res.ok) lastRefreshAt = Date.now();
        return res.ok;
      })
      .catch(() => false)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

function onSessionDead(): void {
  if (sessionDead) return;
  sessionDead = true;
  const here = window.location.pathname;
  if (PUBLIC_PATHS.some((p) => here === p || here.startsWith(`${p}/`))) return;
  // Full reload: AuthContext re-reads /auth/me and starts logged out.
  window.location.assign('/login');
}

window.fetch = async (input: RequestInfo | URL, init: RequestInit = {}) => {
  const finalInit: RequestInit = { ...init, credentials: init.credentials ?? 'include' };
  const url = requestUrl(input);

  if (!isRefreshable(url) || !isReplayable(finalInit)) {
    return originalFetch(input, finalInit);
  }

  // A Request's body can be read once: keep a copy for the retry.
  const retryInput = input instanceof Request ? input.clone() : input;
  const startedAt = Date.now();

  const res = await originalFetch(input, finalInit);
  if (res.status !== 401) return res;

  // Sent before a refresh that has since completed: the new token is already
  // in the session, so just retry.
  const refreshed = startedAt < lastRefreshAt ? true : await refreshSession(url.origin);
  if (!refreshed) {
    onSessionDead();
    return res;
  }

  return originalFetch(retryInput, finalInit);
};

export {};
